import { useEffect, useRef, useState } from "react";

import { radius, shadow, tokens } from "../styles/theme";

export type UndoToastProps = {
  label: string;
  onUndo: () => void;
  onDismiss: () => void;
  durationMs?: number;
};

export function UndoToast({ label, onUndo, onDismiss, durationMs = 5000 }: UndoToastProps) {
  const [paused, setPaused] = useState(false);
  const startRef = useRef<number>(Date.now());
  const remainingRef = useRef<number>(durationMs);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    const tick = () => {
      timerRef.current = window.setTimeout(() => {
        if (paused) return;
        const elapsed = Date.now() - startRef.current;
        if (elapsed >= remainingRef.current) {
          onDismiss();
        } else {
          tick();
        }
      }, remainingRef.current);
    };
    tick();
    return () => {
      if (timerRef.current != null) window.clearTimeout(timerRef.current);
    };
  }, [paused, onDismiss]);

  return (
    <div
      role="status"
      aria-live="polite"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => {
        remainingRef.current = Math.max(
          1000,
          remainingRef.current - (Date.now() - startRef.current),
        );
        startRef.current = Date.now();
        setPaused(false);
      }}
      style={{
        position: "fixed",
        left: "50%",
        transform: "translateX(-50%)",
        bottom: "calc(92px + env(safe-area-inset-bottom))",
        padding: "10px 10px 10px 18px",
        minHeight: "48px",
        background: tokens.ink,
        color: "#f5efdf",
        borderRadius: radius.md,
        boxShadow: shadow.lg,
        display: "flex",
        gap: "16px",
        alignItems: "center",
        fontSize: "14px",
        zIndex: 30,
      }}
    >
      <span>{label}</span>
      <button
        onClick={onUndo}
        style={{
          background: "transparent",
          border: "none",
          color: tokens.sageSoft,
          fontWeight: 600,
          padding: "8px 12px",
          minHeight: "40px",
          minWidth: "60px",
          cursor: "pointer",
          fontFamily: "inherit",
          fontSize: "13px",
          letterSpacing: "0.03em",
          borderRadius: radius.sm,
          transition: "background 0.15s ease",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = "rgba(255,255,255,0.08)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = "transparent";
        }}
      >
        Undo
      </button>
    </div>
  );
}
